import { lazy } from "react";
import { Route } from "react-router-dom";
import ProtectedRoute from "@/components/ProtectedRoute";

// Lazy load pages
const Index = lazy(() => import("./pages/Index"));
const Auth = lazy(() => import("./pages/Auth"));
const SignIn = lazy(() => import("./pages/SignIn"));
const SignUp = lazy(() => import("./pages/SignUp"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const Survey = lazy(() => import("./pages/SmartMatching"));
const Matches = lazy(() => import("./pages/Matches"));
const Chat = lazy(() => import("./pages/Chat"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Collaborations = lazy(() => import("./pages/Collaborations"));
const CollaborationDetails = lazy(() => import("./pages/CollaborationDetails"));
const Projects = lazy(() => import("./pages/Projects"));
const ProjectDetails = lazy(() => import("./pages/ProjectDetails"));
const EditorPortfolio = lazy(() => import("./pages/EditorPortfolio"));
const BecomeEditor = lazy(() => import("./pages/BecomeEditor"));
const EditorOnboarding = lazy(() => import("./pages/EditorOnboarding"));
const EditorDashboard = lazy(() => import("./pages/EditorDashboard"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const HowItWorks = lazy(() => import("./pages/HowItWorks"));
const FindEditor = lazy(() => import("./pages/FindEditor"));
const FileBrowser = lazy(() => import("./pages/FileBrowser"));
const Portfolio = lazy(() => import("./pages/Portfolio"));
const Invoices = lazy(() => import("./pages/Invoices"));
const ContractView = lazy(() => import("./pages/ContractView"));
const Achievements = lazy(() => import("./pages/Achievements"));
const Compare = lazy(() => import("./pages/Compare"));
const Hub = lazy(() => import("./pages/Hub"));
const PostDetail = lazy(() => import("./pages/PostDetail"));
const Notifications = lazy(() => import("./pages/Notifications"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const EditorTutorial = lazy(() => import("./pages/EditorTutorial"));
const ModerationPanel = lazy(() => import("./pages/ModerationPanel"));
const PlatformAnalytics = lazy(() => import("./pages/PlatformAnalytics"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Cookies = lazy(() => import("./pages/Cookies"));
const Help = lazy(() => import("./pages/Help"));
const NotFound = lazy(() => import("./pages/NotFound"));
const EmailSettings = lazy(() => import("./pages/EmailSettings"));
const RefundPolicy = lazy(() => import("./pages/RefundPolicy"));
const AccountSettings = lazy(() => import("./pages/AccountSettings"));
const AdminEmailLogs = lazy(() => import("./pages/AdminEmailLogs"));
const Marketplace = lazy(() => import("./pages/Marketplace"));
const MarketplaceTemplate = lazy(() => import("./pages/MarketplaceTemplate"));
const MonitoringDashboard = lazy(() => import("./pages/MonitoringDashboard"));
const ContractTemplates = lazy(() => import("./pages/ContractTemplates"));
const CampaignEditor = lazy(() => import("./pages/CampaignEditor"));
const Profile = lazy(() => import("./pages/Profile"));
const GoogleOAuthCallback = lazy(() => import("./pages/GoogleOAuthCallback"));
const ChooseUsername = lazy(() => import("./pages/ChooseUsername"));
const ChooseRole = lazy(() => import("./pages/ChooseRole"));

const Workspaces = lazy(() => import("./pages/Workspaces"));
const CompleteProfile = lazy(() => import("./pages/CompleteProfile"));
const Messages = lazy(() => import("./pages/Messages"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const ProjectBrief = lazy(() => import("./pages/ProjectBrief"));

// SEO Service Landing Pages
const YouTubeVideoEditing = lazy(() => import("./pages/services/YouTubeVideoEditing"));
const SocialMediaVideoEditing = lazy(() => import("./pages/services/SocialMediaVideoEditing"));
const CorporateVideoEditing = lazy(() => import("./pages/services/CorporateVideoEditing"));
const WeddingVideoEditing = lazy(() => import("./pages/services/WeddingVideoEditing"));

// Marketing Landing Pages
const JoinAsEditor = lazy(() => import("./pages/JoinAsEditor"));

export const appRoutes = (
  <>
    {/* Public routes */}
    <Route path="/" element={<Index />} />
    
    <Route path="/auth" element={<Auth />} />
    <Route path="/sign-in" element={<SignIn />} />
    <Route path="/sign-up" element={<SignUp />} />
    <Route path="/auth/callback" element={<AuthCallback />} />
    <Route path="/auth/google-callback" element={<GoogleOAuthCallback />} />
    <Route path="/choose-username" element={<ChooseUsername />} />
    <Route path="/choose-role" element={<ChooseRole />} />
    <Route path="/reset-password" element={<ResetPassword />} />
    <Route path="/survey" element={<Survey />} />
    <Route path="/matches" element={<Matches />} />
    <Route path="/project-brief" element={<ProjectBrief />} />
    <Route path="/editor/:id" element={<EditorPortfolio />} />
    <Route path="/become-editor" element={<BecomeEditor />} />
    <Route path="/how-it-works" element={<HowItWorks />} />
    <Route path="/find-editor" element={<FindEditor />} />
    <Route path="/compare" element={<Compare />} />
    <Route path="/hub" element={<Hub />} />
    <Route path="/hub/post/:postId" element={<PostDetail />} />
    <Route path="/terms" element={<Terms />} />
    <Route path="/privacy" element={<Privacy />} />
    <Route path="/cookies" element={<Cookies />} />
    <Route path="/help" element={<Help />} />
    <Route path="/refund-policy" element={<RefundPolicy />} />
    <Route path="/marketplace" element={<Marketplace />} />
    <Route path="/marketplace/:templateId" element={<MarketplaceTemplate />} />
    <Route path="/profile/:userId" element={<Profile />} />

    {/* SEO Service Landing Pages */}
    <Route path="/services/youtube-video-editing" element={<YouTubeVideoEditing />} />
    <Route path="/services/social-media-video-editing" element={<SocialMediaVideoEditing />} />
    <Route path="/services/corporate-video-editing" element={<CorporateVideoEditing />} />
    <Route path="/services/wedding-video-editing" element={<WeddingVideoEditing />} />

    {/* Marketing Landing Pages */}
    <Route path="/join-as-editor" element={<JoinAsEditor />} />

    {/* Protected routes */}
    <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
    <Route path="/chat/:editorId" element={<ProtectedRoute><Chat /></ProtectedRoute>} />
    <Route path="/workspaces" element={<ProtectedRoute><Workspaces /></ProtectedRoute>} />
    <Route path="/messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
    <Route path="/collaborations" element={<ProtectedRoute><Workspaces /></ProtectedRoute>} />
    <Route path="/collaboration/:collaborationId" element={<ProtectedRoute><CollaborationDetails /></ProtectedRoute>} />
    <Route path="/projects" element={<ProtectedRoute><Projects /></ProtectedRoute>} />
    <Route path="/project/:projectId" element={<ProtectedRoute><ProjectDetails /></ProtectedRoute>} />
    <Route path="/editor-onboarding" element={<ProtectedRoute><EditorOnboarding /></ProtectedRoute>} />
    <Route path="/editor-dashboard" element={<ProtectedRoute><EditorDashboard /></ProtectedRoute>} />
    <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
    <Route path="/admin/emails" element={<ProtectedRoute><AdminEmailLogs /></ProtectedRoute>} />
    <Route path="/admin/monitoring" element={<ProtectedRoute><MonitoringDashboard /></ProtectedRoute>} />
    <Route path="/admin/campaign/:id" element={<ProtectedRoute><CampaignEditor /></ProtectedRoute>} />
    <Route path="/file-browser" element={<ProtectedRoute><FileBrowser /></ProtectedRoute>} />
    <Route path="/files" element={<ProtectedRoute><FileBrowser /></ProtectedRoute>} />
    <Route path="/portfolio" element={<ProtectedRoute><Portfolio /></ProtectedRoute>} />
    <Route path="/invoices" element={<ProtectedRoute><Invoices /></ProtectedRoute>} />
    <Route path="/contract/:contractId" element={<ProtectedRoute><ContractView /></ProtectedRoute>} />
    <Route path="/achievements" element={<ProtectedRoute><Achievements /></ProtectedRoute>} />
    <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
    <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
    <Route path="/editor-tutorial" element={<ProtectedRoute><EditorTutorial /></ProtectedRoute>} />
    <Route path="/moderation" element={<ProtectedRoute><ModerationPanel /></ProtectedRoute>} />
    <Route path="/analytics" element={<ProtectedRoute><PlatformAnalytics /></ProtectedRoute>} />
    <Route path="/settings/email" element={<ProtectedRoute><EmailSettings /></ProtectedRoute>} />
    <Route path="/settings/account" element={<ProtectedRoute><AccountSettings /></ProtectedRoute>} />
    <Route path="/templates" element={<ProtectedRoute><ContractTemplates /></ProtectedRoute>} />
    <Route path="/partnerships" element={<ProtectedRoute><Workspaces /></ProtectedRoute>} />
    <Route path="/complete-profile" element={<ProtectedRoute><CompleteProfile /></ProtectedRoute>} />

    {/* 404 */}
    <Route path="*" element={<NotFound />} />
  </>
);
